import { useEffect, useState, useCallback } from 'react'
import {
  ClipboardList, Clock, FileClock, CheckCircle2, XCircle, Search, Eye, X,
  Check, MessageSquare, Download, History, Trash2, Plus, PencilLine,
} from 'lucide-react'
import api from '../api/client'
import Pagination from '../components/Pagination'
import Loader from '../components/Loader'
import SearchableSelect from '../components/SearchableSelect'
import { confirmModal, successModal } from '../components/ui'

// Réplica fiel da Fila de Análise original (Painel ADM).
const STATUS = {
  rascunho: { label: 'Rascunho', cls: 'bg-neutral-100 text-neutral-700 border-neutral-200' },
  analise: { label: 'Em Análise', cls: 'bg-primary-100 text-primary-800 border-primary-300' },
  correcao: { label: 'Correção Solicitada', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  doc_pendente: { label: 'Documentação Pendente', cls: 'bg-accent-100 text-accent-800 border-accent-300' },
  aprovado: { label: 'Aprovado', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  reprovado: { label: 'Reprovado', cls: 'bg-red-100 text-red-800 border-red-300' },
  conexao_usina: { label: 'Envio para a Usina', cls: 'bg-sky-100 text-sky-800 border-sky-300' },
  conexao_rateio_proc: { label: 'Rateio em Processamento', cls: 'bg-sky-100 text-sky-800 border-sky-300' },
  conexao_rateio_enviado: { label: 'Rateio Enviado', cls: 'bg-sky-100 text-sky-800 border-sky-300' },
  conexao_concessionaria: { label: 'Aguardando Concessionária', cls: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  conexao_ativacao: { label: 'Em Ativação', cls: 'bg-violet-100 text-violet-800 border-violet-300' },
  ativo: { label: 'Recebendo Créditos', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  perdido: { label: 'Perdido', cls: 'bg-neutral-200 text-neutral-600 border-neutral-300' },
}
const MOTIVOS = [
  { value: 'documento_invalido', label: 'Documento inválido' },
  { value: 'cliente_inelegivel', label: 'Cliente inelegível' },
  { value: 'dados_inconsistentes', label: 'Dados inconsistentes' },
  { value: 'outros', label: 'Outros' },
]
// Catálogo de campos que o admin pode liberar para correção (doc "Painel de Notificações")
const CAMPOS = [
  { value: 'nome', label: 'Nome do Cliente' },
  { value: 'cpf_cnpj', label: 'CPF/CNPJ' },
  { value: 'telefone', label: 'Telefone' },
  { value: 'email', label: 'E-mail' },
  { value: 'endereco', label: 'Endereço' },
  { value: 'distribuidora', label: 'Distribuidora' },
  { value: 'gerador_parceiro', label: 'Gerador Parceiro' },
  { value: 'produto', label: 'Produto' },
  { value: 'uc', label: 'UC (Nº Instalação)' },
  { value: 'tipo_instalacao', label: 'Tipo de Instalação' },
  { value: 'media_consumo', label: 'Consumo médio' },
  { value: 'valor_fatura', label: 'Valor da fatura' },
  { value: 'conta_luz', label: 'Conta de Luz' },
  { value: 'doc_foto', label: 'Documento com Foto' },
  { value: 'documentos', label: 'Outros documentos' },
]

const fmtData = (v) => (v ? new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-')
const fmtMoney = (v) => (v ? `R$ ${Number(v).toFixed(2)}` : '-')

function StatCard({ label, value, icon: Icon, color, active, onClick }) {
  const c = {
    primary: { bg: 'bg-gradient-to-br from-primary-500 to-primary-600', ring: 'ring-primary-300', text: 'text-primary-600' },
    accent: { bg: 'bg-gradient-to-br from-accent-500 to-accent-600', ring: 'ring-accent-300', text: 'text-accent-600' },
    emerald: { bg: 'bg-gradient-to-br from-emerald-500 to-emerald-600', ring: 'ring-emerald-300', text: 'text-emerald-600' },
    red: { bg: 'bg-gradient-to-br from-red-500 to-red-600', ring: 'ring-red-300', text: 'text-red-600' },
  }[color] || {}
  return (
    <button onClick={onClick}
      className={`bg-white rounded-2xl p-4 shadow-sm border border-neutral-200 text-left transition-all hover:-translate-y-0.5 ${active ? `ring-2 ${c.ring}` : 'hover:shadow-md'}`}>
      <div className="flex items-start justify-between mb-2">
        <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center shadow-sm`}>
          <Icon size={18} className="text-white" strokeWidth={2.5} />
        </div>
      </div>
      <p className="text-2xl font-heading font-extrabold text-graphite">{value}</p>
      <p className={`text-xs font-bold uppercase tracking-wider mt-0.5 ${c.text}`}>{label}</p>
    </button>
  )
}

/* ===== Modal de detalhe (dados + documentos + histórico + ações) ===== */
function DetalheModal({ item, onClose, onDone }) {
  const [det, setDet] = useState(null)
  const [docs, setDocs] = useState([])
  const [hist, setHist] = useState([])
  const [busy, setBusy] = useState(false)
  const [modo, setModo] = useState(null) // null | 'correcao' | 'reprovar'
  const [campos, setCampos] = useState([])
  const [mensagem, setMensagem] = useState('')
  const [motivo, setMotivo] = useState('')
  const [obs, setObs] = useState('')
  const [obsInt, setObsInt] = useState('')
  const [msg, setMsg] = useState('')
  const [edit, setEdit] = useState(false)
  const [ed, setEd] = useState({})
  const [dists, setDists] = useState([])
  const [gers, setGers] = useState([])
  const [ofs, setOfs] = useState([])

  function loadDocs() { api.get(`/contratos-adesao/${item.id}/documentos`).then((r) => setDocs(r.data.items || r.data.data || [])).catch(() => {}) }
  useEffect(() => {
    api.get(`/contratos-adesao/${item.id}`).then((r) => {
      setDet(r.data); setObsInt(r.data.observacoes_internas || '')
      setEd({ distribuidora_id: r.data.distribuidora_id || '', geradora_id: r.data.geradora_id || '', oferta_id: r.data.oferta_id || '',
        numero_instalacao: r.data.numero_instalacao || '', tipo_instalacao: r.data.tipo_instalacao || '',
        media_consumo: r.data.media_consumo || '', valor_fatura: r.data.valor_fatura || '' })
    }).catch(() => {})
    loadDocs()
    api.get(`/contratos-adesao/${item.id}/historico`).then((r) => setHist(r.data.items || r.data.data || [])).catch(() => {})
    api.get('/distribuidoras?limit=200').then((r) => setDists(r.data.data || r.data || [])).catch(() => {})
    api.get('/geradoras?limit=200').then((r) => setGers(r.data.data || r.data || [])).catch(() => {})
    api.get('/ofertas/publico').then((r) => setOfs(r.data || [])).catch(() => {})
  }, [item.id])

  async function salvarEdicao() {
    setBusy(true)
    try {
      await api.put(`/contratos-adesao/${item.id}`, {
        distribuidora_id: ed.distribuidora_id ? Number(ed.distribuidora_id) : null,
        geradora_id: ed.geradora_id ? Number(ed.geradora_id) : null,
        oferta_id: ed.oferta_id ? Number(ed.oferta_id) : null,
        numero_instalacao: ed.numero_instalacao, tipo_instalacao: ed.tipo_instalacao,
        media_consumo: ed.media_consumo ? Number(ed.media_consumo) : null,
        valor_fatura: ed.valor_fatura ? Number(ed.valor_fatura) : null,
      })
      const r = await api.get(`/contratos-adesao/${item.id}`); setDet(r.data); setEdit(false)
      await successModal('Dados atualizados.')
    } catch (e) { setMsg(e?.response?.data?.detail || 'Erro ao salvar') } finally { setBusy(false) }
  }
  async function removerDoc(docId) {
    if (!(await confirmModal('Remover este documento?', { title: 'Remover documento', okLabel: 'Remover' }))) return
    setBusy(true)
    try { await api.delete(`/contratos-adesao/${item.id}/documentos/${docId}`); loadDocs() }
    catch (e) { setMsg(e?.response?.data?.detail || 'Erro ao remover') } finally { setBusy(false) }
  }
  async function addDoc(e) {
    const file = e.target.files?.[0]; if (!file) return
    const tipo = window.prompt('Tipo do documento (ex.: conta_luz, doc_foto, contrato_social):', 'documento') || 'documento'
    setBusy(true)
    try { const fd = new FormData(); fd.append('tipo', tipo); fd.append('file', file); await api.post(`/contratos-adesao/${item.id}/documentos`, fd); loadDocs() }
    catch (e2) { setMsg(e2?.response?.data?.detail || 'Erro ao anexar') } finally { setBusy(false) }
  }

  async function salvarObs() {
    setBusy(true)
    try { await api.put(`/contratos-adesao/${item.id}`, { observacoes_internas: obsInt }); await successModal('Observação interna salva.') }
    catch (e) { setMsg(e?.response?.data?.detail || 'Erro ao salvar observação') } finally { setBusy(false) }
  }
  async function validarDoc(docId, status) {
    let motivo = null
    if (status === 'rejeitado') {
      motivo = window.prompt('Motivo da rejeição do documento (opcional):') || ''
    }
    setBusy(true)
    try {
      await api.post(`/contratos-adesao/${item.id}/documentos/${docId}/validar`, { status, motivo })
      const r = await api.get(`/contratos-adesao/${item.id}/documentos`)
      setDocs(r.data.items || [])
    } catch (e) { setMsg(e?.response?.data?.detail || 'Erro ao validar documento') } finally { setBusy(false) }
  }

  const podeAgir = ['analise', 'correcao', 'doc_pendente'].includes(item.status)
  const d = det || item
  const st = STATUS[item.status] || { label: item.status, cls: 'bg-neutral-100 text-neutral-700' }

  async function aprovar() {
    if (!(await confirmModal('Ao aprovar, o sistema gera os Pontos (PV), atualiza VQ/VT, distribui as comissões e bônus na rede e envia o cliente para a Fila de Conexão. Confirmar?', { title: 'Aprovar cadastro', okLabel: 'Aprovar' }))) return
    setBusy(true)
    try {
      const r = await api.post(`/contratos-adesao/${item.id}/aprovar`)
      await successModal(`PV ${r.data.pv || 0} gerado. Comissões distribuídas: R$ ${(r.data.comissao || 0).toFixed(2)}. Cliente enviado à Fila de Conexão.`, { title: 'Cadastro aprovado!' })
      onDone()
    } catch (e) { setMsg(e?.response?.data?.detail || 'Erro ao aprovar') } finally { setBusy(false) }
  }
  async function enviarCorrecao() {
    if (!mensagem.trim()) { setMsg('Escreva a mensagem'); return }
    setBusy(true)
    try { await api.post(`/contratos-adesao/${item.id}/solicitar-correcao`, { campos, mensagem }); await successModal('Solicitação de correção enviada ao licenciado.', { title: 'Enviado!' }); onDone() }
    catch (e) { setMsg(e?.response?.data?.detail || 'Erro') } finally { setBusy(false) }
  }
  async function reprovar() {
    if (!motivo) { setMsg('Selecione o motivo'); return }
    setBusy(true)
    try { await api.post(`/contratos-adesao/${item.id}/reprovar`, { motivo, observacao: obs }); await successModal('Cadastro reprovado.', { title: 'Concluído' }); onDone() }
    catch (e) { setMsg(e?.response?.data?.detail || 'Erro') } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 bg-gradient-to-r from-primary-50 to-accent-50 border-b border-primary-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center text-graphite font-bold flex-shrink-0">
            {(d.cliente_nome || '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-extrabold text-graphite truncate">{d.cliente_nome || 'Sem cliente'}</h3>
            <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold border ${st.cls}`}>{st.label}</span>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-graphite"><X size={20} /></button>
        </div>

        <div className="p-5 overflow-y-auto tc-scroll space-y-5">
          {msg && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{msg}</p>}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="CPF/CNPJ" value={d.cliente_cpf_cnpj} />
            <Info label="Produto" value={d.plano_descricao || d.oferta_nome || d.tipo_conexao} />
            <Info label="Distribuidora" value={d.distribuidora_nome || d.distribuidora?.nome} />
            <Info label="Gerador Parceiro" value={d.geradora_nome} />
            <Info label="Licenciado responsável" value={d.licenciado_nome} />
            <Info label="Qual desconto" value={d.desconto} />
            <Info label="Estado" value={d.estado || d.distribuidora?.estado} />
            <Info label="Cidade" value={d.cidade} />
            <Info label="Valor da fatura" value={fmtMoney(d.valor_fatura)} />
            <Info label="Nº Instalação (UC)" value={d.numero_instalacao} />
            <Info label="Data do cadastro" value={fmtData(d.created_at)} />
            <Info label="Data do envio / análise" value={fmtData(d.updated_at || d.created_at)} />
          </div>

          <div>
            <button onClick={() => setEdit((v) => !v)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-700 hover:underline">
              <PencilLine size={13} /> {edit ? 'Fechar edição' : 'Editar dados (admin)'}
            </button>
            {edit && (
              <div className="mt-2 grid grid-cols-2 gap-2 bg-neutral-50 rounded-xl p-3 border border-neutral-100">
                <div><p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">Distribuidora</p>
                  <SearchableSelect value={ed.distribuidora_id} onChange={(v) => setEd({ ...ed, distribuidora_id: v })} options={dists.map((x) => ({ value: x.id, label: x.nome }))} placeholder="—" /></div>
                <div><p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">Gerador Parceiro</p>
                  <SearchableSelect value={ed.geradora_id} onChange={(v) => setEd({ ...ed, geradora_id: v })} options={gers.map((x) => ({ value: x.id, label: x.nome }))} placeholder="—" /></div>
                <div className="col-span-2"><p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">Produto</p>
                  <SearchableSelect value={ed.oferta_id} onChange={(v) => setEd({ ...ed, oferta_id: v })} options={ofs.map((x) => ({ value: x.id, label: x.nome }))} placeholder="—" /></div>
                <div><p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">UC (Nº Instalação)</p>
                  <input className="w-full border border-neutral-200 rounded-lg px-2 py-1.5 text-sm" value={ed.numero_instalacao} onChange={(e) => setEd({ ...ed, numero_instalacao: e.target.value })} /></div>
                <div><p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">Tipo de Instalação</p>
                  <input className="w-full border border-neutral-200 rounded-lg px-2 py-1.5 text-sm" value={ed.tipo_instalacao} onChange={(e) => setEd({ ...ed, tipo_instalacao: e.target.value })} /></div>
                <div><p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">Consumo (kWh)</p>
                  <input type="number" className="w-full border border-neutral-200 rounded-lg px-2 py-1.5 text-sm" value={ed.media_consumo} onChange={(e) => setEd({ ...ed, media_consumo: e.target.value })} /></div>
                <div><p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">Valor da fatura (R$)</p>
                  <input type="number" className="w-full border border-neutral-200 rounded-lg px-2 py-1.5 text-sm" value={ed.valor_fatura} onChange={(e) => setEd({ ...ed, valor_fatura: e.target.value })} /></div>
                <div className="col-span-2 flex justify-end">
                  <button onClick={salvarEdicao} disabled={busy} className="rounded-lg bg-accent-500 text-white text-xs font-bold px-4 py-1.5">Salvar alterações</button>
                </div>
              </div>
            )}
          </div>

          {/* Observações internas (só admin) */}
          <div className="bg-neutral-50 rounded-xl p-3 border border-neutral-100">
            <p className="text-[11px] uppercase tracking-wider text-neutral-400 mb-1">Observações internas (não visível ao licenciado)</p>
            <textarea value={obsInt} onChange={(e) => setObsInt(e.target.value)} rows={2}
              placeholder="Anotações internas da análise..."
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400 bg-white" />
            <div className="flex justify-end mt-1.5">
              <button onClick={salvarObs} disabled={busy} className="text-xs font-semibold text-accent-700 hover:underline">Salvar observação</button>
            </div>
          </div>

          {d.motivo_correcao && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 whitespace-pre-line"><b>Correção solicitada:</b> {d.motivo_correcao}</p>
          )}
          {d.motivo_reprovacao && (
            <p className="text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2"><b>Motivo da reprovação:</b> {d.motivo_reprovacao}</p>
          )}

          {d.dados_customizados && Object.keys(d.dados_customizados).length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">Dados do formulário</p>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(d.dados_customizados).map(([k, v]) => (
                  <Info key={k} label={k.replace(/_/g, ' ')} value={v == null || v === '' ? '-' : String(v)} />
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2 flex items-center gap-1.5"><Download size={13} /> Documentos</p>
            {docs.length === 0 ? <p className="text-sm text-neutral-400">Nenhum documento.</p> : (
              <div className="space-y-1.5">
                {docs.map((doc) => {
                  const dstatus = doc.status || 'pendente'
                  const badge = { validado: 'bg-emerald-100 text-emerald-700', rejeitado: 'bg-red-100 text-red-700', pendente: 'bg-neutral-100 text-neutral-500' }[dstatus]
                  return (
                    <div key={doc.id} className="rounded-lg border border-neutral-100 px-3 py-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <a href={`${api.defaults.baseURL}/contratos-adesao/${item.id}/documentos/${doc.id}/download`} target="_blank" rel="noreferrer"
                          className="flex items-center gap-2 text-sm text-accent-700 hover:underline flex-1 min-w-0">
                          <Download size={14} /> <span className="truncate">{(doc.tipo || '').replace(/_/g, ' ') || doc.nome_arquivo || `Documento ${doc.id}`}</span>
                        </a>
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${badge}`}>{dstatus}</span>
                        <button onClick={() => validarDoc(doc.id, 'validado')} disabled={busy} title="Validar" className="p-1 rounded hover:bg-emerald-50 text-emerald-600"><Check size={15} /></button>
                        <button onClick={() => validarDoc(doc.id, 'rejeitado')} disabled={busy} title="Rejeitar" className="p-1 rounded hover:bg-red-50 text-red-600"><XCircle size={15} /></button>
                        <button onClick={() => removerDoc(doc.id)} disabled={busy} title="Remover" className="p-1 rounded hover:bg-neutral-100 text-neutral-500"><Trash2 size={15} /></button>
                      </div>
                      {doc.motivo && <p className="text-[11px] text-red-600 mt-1">{doc.motivo}</p>}
                    </div>
                  )
                })}
              </div>
            )}
            <label className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-accent-700 hover:underline cursor-pointer">
              <Plus size={13} /> Adicionar / substituir documento
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={addDoc} />
            </label>
          </div>

          {hist.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2 flex items-center gap-1.5"><History size={13} /> Histórico</p>
              <div className="space-y-1.5">
                {hist.map((h, i) => (
                  <div key={i} className="text-xs text-neutral-600 flex gap-2">
                    <span className="text-neutral-400 whitespace-nowrap">{fmtData(h.created_at)}</span>
                    <span>
                      {h.mensagem || (STATUS[h.status_novo]?.label) || h.acao}
                      {h.usuario_nome ? <span className="text-neutral-400"> — {h.usuario_nome}</span> : null}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Forms de ação */}
          {modo === 'correcao' && (
            <div className="bg-accent-50/50 rounded-xl p-4 border border-accent-100 space-y-3">
              <p className="text-sm font-semibold text-graphite">Solicitar correção</p>
              <div className="flex flex-wrap gap-2">
                {CAMPOS.map((c) => (
                  <button key={c.value} onClick={() => setCampos((p) => p.includes(c.value) ? p.filter((x) => x !== c.value) : [...p, c.value])}
                    className={`px-2.5 py-1 rounded-lg text-xs border ${campos.includes(c.value) ? 'bg-accent-500 text-white border-accent-500' : 'border-neutral-200 text-neutral-600'}`}>{c.label}</button>
                ))}
              </div>
              <textarea value={mensagem} onChange={(e) => setMensagem(e.target.value)} placeholder="Mensagem ao licenciado..." rows={2}
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400" />
            </div>
          )}
          {modo === 'reprovar' && (
            <div className="bg-red-50/50 rounded-xl p-4 border border-red-100 space-y-3">
              <p className="text-sm font-semibold text-graphite">Reprovar cadastro</p>
              <SearchableSelect value={motivo} onChange={setMotivo} options={MOTIVOS} placeholder="Selecione o motivo..." />
              <textarea value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Observação (opcional)" rows={2}
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400" />
            </div>
          )}
        </div>

        {podeAgir && (
          <div className="px-5 py-4 border-t border-neutral-100 flex flex-wrap gap-2 justify-end bg-neutral-50">
            {modo === null && (
              <>
                <button onClick={() => setModo('correcao')} disabled={busy} className="px-4 py-2 rounded-xl border border-accent-200 text-accent-700 text-sm font-semibold hover:bg-accent-50 flex items-center gap-1.5"><MessageSquare size={15} /> Solicitar correção</button>
                <button onClick={() => setModo('reprovar')} disabled={busy} className="px-4 py-2 rounded-xl border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50 flex items-center gap-1.5"><XCircle size={15} /> Reprovar</button>
                <button onClick={aprovar} disabled={busy} className="px-5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-bold shadow-md hover:brightness-95 flex items-center gap-1.5"><Check size={15} /> Aprovar</button>
              </>
            )}
            {modo === 'correcao' && (
              <>
                <button onClick={() => setModo(null)} className="px-4 py-2 rounded-xl border border-neutral-200 text-neutral-500 text-sm">Voltar</button>
                <button onClick={enviarCorrecao} disabled={busy} className="px-5 py-2 rounded-xl bg-gradient-to-r from-accent-500 to-accent-600 text-white text-sm font-bold shadow-md">Enviar solicitação</button>
              </>
            )}
            {modo === 'reprovar' && (
              <>
                <button onClick={() => setModo(null)} className="px-4 py-2 rounded-xl border border-neutral-200 text-neutral-500 text-sm">Voltar</button>
                <button onClick={reprovar} disabled={busy} className="px-5 py-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white text-sm font-bold shadow-md">Confirmar reprovação</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
function Info({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-neutral-400">{label}</p>
      <p className="text-sm text-graphite font-medium">{value || '-'}</p>
    </div>
  )
}

export default function FilaAnalise() {
  const [stats, setStats] = useState(null)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(20)
  const [pages, setPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [busca, setBusca] = useState('')
  const [debounced, setDebounced] = useState('')
  const [status, setStatus] = useState('analise')
  const [lics, setLics] = useState([])
  const [licId, setLicId] = useState('')
  const [sel, setSel] = useState(null)

  useEffect(() => { const t = setTimeout(() => setDebounced(busca), 300); return () => clearTimeout(t) }, [busca])
  useEffect(() => { api.get('/licenciados?limit=100').then((r) => setLics(r.data.data || [])).catch(() => {}) }, [])

  const loadStats = useCallback(() => { api.get('/contratos-adesao/_stats/fila').then((r) => setStats(r.data)).catch(() => {}) }, [])
  const loadRows = useCallback(() => {
    setLoading(true)
    const params = { page, limit: size }
    if (status && status !== 'todos') params.status = status
    if (licId) params.licenciado_id = licId
    if (debounced) params.search = debounced
    api.get('/contratos-adesao', { params }).then((r) => {
      setRows(r.data.data || []); setPages(r.data.pages || 1); setTotal(r.data.total || 0)
    }).catch(() => setRows([])).finally(() => setLoading(false))
  }, [page, size, status, licId, debounced])

  useEffect(() => { loadStats() }, [loadStats])
  useEffect(() => { loadRows() }, [loadRows])

  const filtrar = (s) => { setStatus(s); setPage(1) }
  const aoConcluir = () => { setSel(null); loadStats(); loadRows() }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-heading font-extrabold text-graphite flex items-center gap-2">
          <ClipboardList size={24} className="text-accent-500" /> Painel ADM — Fila de Análise
        </h1>
        <p className="text-sm text-neutral-500 mt-1">Validação de cadastros enviados por licenciados</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Em Análise" value={stats?.em_analise ?? '-'} icon={Clock} color="primary" active={status === 'analise'} onClick={() => filtrar('analise')} />
        <StatCard label="Correção Solicitada" value={stats?.correcao ?? '-'} icon={MessageSquare} color="accent" active={status === 'correcao'} onClick={() => filtrar('correcao')} />
        <StatCard label="Doc Pendente" value={stats?.doc_pendente ?? '-'} icon={FileClock} color="accent" active={status === 'doc_pendente'} onClick={() => filtrar('doc_pendente')} />
        <StatCard label="Aprovados Hoje" value={stats?.aprovados_hoje ?? '-'} icon={CheckCircle2} color="emerald" active={status === 'aprovado'} onClick={() => filtrar('aprovado')} />
        <StatCard label="Reprovados" value={stats?.reprovados ?? '-'} icon={XCircle} color="red" active={status === 'reprovado'} onClick={() => filtrar('reprovado')} />
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-3 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[220px] relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input value={busca} onChange={(e) => { setBusca(e.target.value); setPage(1) }} placeholder="Buscar por nome, CPF/CNPJ, UC, telefone ou licenciado..."
            className="w-full pl-10 pr-3 py-2 rounded-xl border border-neutral-200 text-sm focus:border-accent-400 focus:ring-2 focus:ring-accent-100 outline-none" />
        </div>
        <SearchableSelect value={status} onChange={(v) => { setStatus(v); setPage(1) }} emptyOption={false} className="w-44"
          options={[
            { value: 'todos', label: 'Todos os status' },
            { value: 'analise', label: 'Em Análise' },
            { value: 'correcao', label: 'Correção Solicitada' },
            { value: 'doc_pendente', label: 'Documentação Pendente' },
            { value: 'aprovado', label: 'Aprovado' },
            { value: 'reprovado', label: 'Reprovado' },
          ]} />
        <SearchableSelect value={licId} onChange={(v) => { setLicId(v); setPage(1) }} emptyLabel="Todos licenciados" className="w-52"
          options={lics.map((l) => ({ value: l.id, label: l.nome }))} />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader text="Carregando fila..." /></div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-primary-50 to-accent-50 border-b border-primary-100">
                  <th className="text-left px-4 py-3 text-xs font-bold text-graphite uppercase tracking-wider">Cliente</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-graphite uppercase tracking-wider">Produto</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-graphite uppercase tracking-wider hidden md:table-cell">Licenciado</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-graphite uppercase tracking-wider hidden lg:table-cell">Fatura</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-graphite uppercase tracking-wider">Status</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-graphite uppercase tracking-wider hidden xl:table-cell">Enviado</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-graphite uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-16 text-neutral-400">
                    <ClipboardList size={40} className="mx-auto mb-2 opacity-30" />
                    <p>Nenhum cadastro nesta fila</p>
                  </td></tr>
                ) : rows.map((v) => {
                  const st = STATUS[v.status] || { label: v.status, cls: 'bg-neutral-100 text-neutral-700' }
                  return (
                    <tr key={v.id} onClick={() => setSel(v)} className="border-b border-neutral-100 hover:bg-primary-50/40 cursor-pointer">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center text-graphite text-xs font-bold flex-shrink-0">{(v.cliente_nome || '?').charAt(0).toUpperCase()}</div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-graphite truncate">{v.cliente_nome || 'Sem cliente'}</p>
                            <p className="text-[11px] text-neutral-400 truncate">{v.cliente_cpf_cnpj || ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className="text-sm text-graphite">{v.plano_descricao || v.oferta_nome || v.tipo_conexao || '-'}</span></td>
                      <td className="px-4 py-3 hidden md:table-cell"><span className="text-xs text-neutral-600">{v.licenciado_nome || '-'}</span></td>
                      <td className="px-4 py-3 text-center text-sm text-neutral-700 hidden lg:table-cell">{fmtMoney(v.valor_fatura)}</td>
                      <td className="px-4 py-3 text-center"><span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${st.cls}`}>{st.label}</span></td>
                      <td className="px-4 py-3 text-center text-xs text-neutral-500 hidden xl:table-cell">{fmtData(v.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={(e) => { e.stopPropagation(); setSel(v) }} className="p-2 rounded-lg hover:bg-accent-100 text-accent-600"><Eye size={16} /></button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {total > 0 && (
            <div className="px-4 py-3 border-t border-neutral-100">
              <Pagination page={page} pages={pages} total={total} pageSize={size} onPage={setPage} onPageSize={(n) => { setSize(n); setPage(1) }} />
            </div>
          )}
        </div>
      )}

      {sel && <DetalheModal item={sel} onClose={() => setSel(null)} onDone={aoConcluir} />}
    </div>
  )
}
